import { useEffect, useState } from "react";
import { fetchPmsConnections } from "../services/pmsConnectionsApi";
import { fetchPmsProviders, type PmsProviderCapabilities, type PmsProviderType } from "../services/pmsApi";

/**
 * The PMS behind a connection: which one it is, and what it can do.
 *
 * The two travel together because a screen that says something about the PMS
 * — "Better Hotel writes a minimum stay only beside a price" — says it by
 * name, about a capability, and a name read from one lookup beside a
 * capability read from another can describe two different properties.
 */
export interface ConnectionProvider {
    providerType: PmsProviderType;
    /** Null where the registry does not describe this PMS. */
    capabilities: PmsProviderCapabilities | null;
}

/**
 * The active PMS connection's provider.
 *
 * Returns `null` while loading and when there is no active connection, so
 * callers can tell "not known yet" from "known to be unsupported" — gating a
 * control on a capability that has not loaded would flicker it disabled on
 * every mount.
 */
export function useActiveProvider(): ConnectionProvider | null {
    return useProvider("active");
}

/**
 * The provider behind one named PMS connection — the property a screen
 * configures, which need not be the active one. The auto-sync dialog is
 * mounted once per property card on the Connections page, and gating its
 * minimum-stay switch on the active property would answer for the wrong PMS on
 * every other card. `null` while loading, as above.
 */
export function useConnectionProvider(connectionId: number): ConnectionProvider | null {
    return useProvider(connectionId);
}

/** What the active PMS connection's provider can do; `null` while unknown. */
export function useActiveProviderCapabilities(): PmsProviderCapabilities | null {
    return useActiveProvider()?.capabilities ?? null;
}

/** What the provider behind one named PMS connection can do; `null` while unknown. */
export function useConnectionProviderCapabilities(
    connectionId: number,
): PmsProviderCapabilities | null {
    return useConnectionProvider(connectionId)?.capabilities ?? null;
}

function useProvider(target: number | "active"): ConnectionProvider | null {
    const [provider, setProvider] = useState<ConnectionProvider | null>(null);

    useEffect(() => {
        let cancelled = false;
        // Wrapped in an async IIFE so a synchronous throw is caught too — this
        // hook decorates a control, and nothing it does should be able to take
        // the surrounding dialog down with it.
        void (async () => {
            try {
                const [connections, providers] = await Promise.all([
                    fetchPmsConnections(),
                    fetchPmsProviders(),
                ]);
                if (cancelled) return;
                const connection = connections.find((c) =>
                    target === "active" ? c.isActive : c.id === target,
                );
                if (!connection) return;
                const described = providers.find((p) => p.providerType === connection.providerType);
                setProvider({
                    providerType: connection.providerType,
                    capabilities: described?.capabilities ?? null,
                });
            } catch {
                // Non-critical: a provider we cannot read stays null, and
                // callers fall back to their permissive default rather than
                // locking the user out of a feature over a failed lookup.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [target]);

    return provider;
}
